// frontend/src/pages/CreatePayment.jsx
// Keeps all PhonePe tabs unchanged + adds Razorpay Checkout & Razorpay Link tabs

import { useState } from 'react';
import Layout from '../components/Layout';
import API from '../api';
import toast from 'react-hot-toast';
import {
  FiCreditCard, FiLink, FiCamera, FiCopy, FiMessageCircle,
  FiMail, FiExternalLink, FiDownload, FiRefreshCw,
  FiCheckCircle, FiInfo, FiCalendar, FiZap,
} from 'react-icons/fi';

// ─────────────────────────────────────────────────────────────────────────────
// Load Razorpay checkout.js on demand (only when needed)
// ─────────────────────────────────────────────────────────────────────────────
const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (document.getElementById('razorpay-script')) { resolve(true); return; }
    const script = document.createElement('script');
    script.id  = 'razorpay-script';
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload  = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

// ─────────────────────────────────────────────────────────────────────────────
// Tab config
// ─────────────────────────────────────────────────────────────────────────────
const GATEWAY_TABS = [
  {
    gateway: 'phonepe',
    id: 'pp_direct',
    label: <><FiCreditCard style={{ marginRight: 6 }} />PhonePe Direct</>,
    desc: 'Redirect customer to PhonePe checkout (UPI only)',
  },
  {
    gateway: 'phonepe',
    id: 'pp_link',
    label: <><FiLink style={{ marginRight: 6 }} />PhonePe Link</>,
    desc: 'Generate a shareable PhonePe payment link (UPI)',
  },
  {
    gateway: 'phonepe',
    id: 'pp_qr',
    label: <><FiCamera style={{ marginRight: 6 }} />QR Code</>,
    desc: 'Generate UPI QR code for in-person payment',
  },
  {
    gateway: 'razorpay',
    id: 'rzp_direct',
    label: <><FiZap style={{ marginRight: 6 }} />Razorpay Checkout</>,
    desc: 'Opens Razorpay modal — Credit/Debit Card, Net Banking, UPI, Wallets & more',
    badge: 'All Methods',
  },
  {
    gateway: 'razorpay',
    id: 'rzp_link',
    label: <><FiLink style={{ marginRight: 6 }} />Razorpay Link</>,
    desc: 'Shareable Razorpay payment link — customer pays by Card / UPI / Netbanking',
    badge: 'All Methods',
  },
];

const initForm = { name: '', phone: '', email: '', amount: '', description: '', eventName: '' };

export default function CreatePayment() {
  const [tab, setTab]           = useState('pp_direct');
  const [form, setForm]         = useState(initForm);
  const [loading, setLoading]   = useState(false);
  const [result, setResult]     = useState(null);
  const [copyDone, setCopyDone] = useState(false);

  const activeTab  = GATEWAY_TABS.find(t => t.id === tab);
  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const reset      = () => { setForm(initForm); setResult(null); };

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = () => {
    if (!form.name.trim())  { toast.error('Customer name is required'); return false; }
    if (!form.phone.trim()) { toast.error('Phone number is required'); return false; }
    if (!/^[6-9]\d{9}$/.test(form.phone)) { toast.error('Enter valid 10-digit mobile number'); return false; }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Enter valid amount'); return false; }
    if (parseFloat(form.amount) > 500000)  { toast.error('Amount cannot exceed ₹5,00,000'); return false; }
    return true;
  };

  // ── PhonePe handlers (original logic — untouched) ─────────────────────────
  const handlePhonePeSubmit = async () => {
    const endpoint =
      tab === 'pp_direct' ? '/payment/initiate'
      : tab === 'pp_link' ? '/payment/generate-link'
      : '/payment/generate-qr';

    const { data } = await API.post(endpoint, form);

    if (tab === 'pp_direct' && data.redirectUrl) {
      toast.success('Redirecting to PhonePe...');
      setTimeout(() => window.open(data.redirectUrl, '_blank'), 800);
      setResult({ type: 'redirect', txnId: data.merchantTransactionId, url: data.redirectUrl, gateway: 'phonepe' });
    } else if (tab === 'pp_link') {
      if (!data.paymentLink) throw new Error('No payment link returned from server');
      toast.success('Payment link generated!');
      setResult({ type: 'link', link: data.paymentLink, txnId: data.merchantTransactionId, gateway: 'phonepe' });
    } else {
      if (!data.qrImage && !data.qrData) throw new Error('No QR data returned from server');
      toast.success('QR code generated!');
      setResult({ type: 'qr', qrData: data.qrData, qrImage: data.qrImage, txnId: data.merchantTransactionId, gateway: 'phonepe' });
    }
  };

  // ── Razorpay: Direct Checkout (opens modal in-page) ───────────────────────
  const handleRazorpayDirect = async () => {
    const loaded = await loadRazorpayScript();
    if (!loaded) { toast.error('Failed to load Razorpay. Check your internet.'); return; }

    const { data } = await API.post('/razorpay/create-order', form);
    if (!data.success) throw new Error(data.message || 'Order creation failed');

    return new Promise((resolve, reject) => {
      const rzp = new window.Razorpay({
        key:         data.razorpayKeyId,
        amount:      data.amount,
        currency:    data.currency,
        order_id:    data.razorpayOrderId,
        name:        'StepOnExp',
        description: form.description || form.eventName || 'Payment',
        prefill: {
          name:    data.customerName,
          email:   data.customerEmail,
          contact: `+91${form.phone}`,
        },
        notes: { merchant_transaction_id: data.merchantTransactionId },
        theme: { color: '#7c5cfc' },
        modal: {
          ondismiss: () => { toast('Payment cancelled'); resolve(null); },
        },
        handler: async (response) => {
          try {
            const verify = await API.post('/razorpay/verify-payment', {
              razorpay_order_id:     response.razorpay_order_id,
              razorpay_payment_id:   response.razorpay_payment_id,
              razorpay_signature:    response.razorpay_signature,
              merchantTransactionId: data.merchantTransactionId,
            });
            if (verify.data.success) {
              toast.success(' Payment verified & confirmed!');
              setResult({
                type: 'success',
                txnId: data.merchantTransactionId,
                razorpayPaymentId: response.razorpay_payment_id,
                gateway: 'razorpay',
              });
              resolve(verify.data);
            } else {
              toast.error('Payment verification failed');
              resolve(null);
            }
          } catch (e) {
            toast.error('Verification error: ' + (e?.response?.data?.message || e.message));
            reject(e);
          }
        },
      });
      rzp.on('payment.failed', (resp) => {
        toast.error('Payment failed: ' + (resp.error?.description || 'Unknown error'));
        resolve(null);
      });
      rzp.open();
    });
  };

  // ── Razorpay: Shareable Payment Link ─────────────────────────────────────
  const handleRazorpayLink = async () => {
    const { data } = await API.post('/razorpay/generate-link', form);
    if (!data.success || !data.paymentLink) throw new Error(data.message || 'Link generation failed');
    toast.success('Razorpay payment link generated!');
    setResult({ type: 'link', link: data.paymentLink, txnId: data.merchantTransactionId, gateway: 'razorpay' });
  };

  // ── Main submit dispatcher ────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    setResult(null);
    try {
      if      (tab === 'rzp_direct') await handleRazorpayDirect();
      else if (tab === 'rzp_link')   await handleRazorpayLink();
      else                           await handlePhonePeSubmit();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopyDone(true);
    toast.success('Copied!');
    setTimeout(() => setCopyDone(false), 2000);
  };

  const downloadQR = () => {
    if (!result?.qrImage) return;
    const a = document.createElement('a');
    a.href = result.qrImage;
    a.download = `QR_${result.txnId}.png`;
    a.click();
    toast.success('QR downloaded!');
  };

  const shareOnWhatsApp = () => {
    const amt = parseFloat(form.amount).toLocaleString('en-IN');
    const msg = result?.type === 'link'
      ? `Hi ${form.name},\n\nPlease complete your payment of ₹${amt}${form.eventName ? ` for *${form.eventName}*` : ''} using this link:\n${result.link}\n\nTransaction ID: ${result.txnId}\n\n— StepOnExp`
      : `Hi ${form.name},\n\nPlease pay ₹${amt}${form.eventName ? ` for *${form.eventName}*` : ''} using the QR code.\n\nTransaction ID: ${result?.txnId}`;
    window.open(`https://wa.me/${form.phone}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const shareViaEmail = () => {
    if (!form.email) return toast.error('Email address is required');
    const amt     = parseFloat(form.amount).toLocaleString('en-IN');
    const subject = `Payment Request${form.eventName ? ` — ${form.eventName}` : ''} - ₹${amt}`;
    const body    = result?.type === 'link'
      ? `Hi ${form.name},\n\nPlease complete your payment of ₹${amt}${form.eventName ? ` for ${form.eventName}` : ''} using this link:\n${result.link}\n\nTransaction ID: ${result.txnId}`
      : `Hi ${form.name},\n\nPlease pay ₹${amt}. Transaction ID: ${result?.txnId}`;
    window.open(`mailto:${form.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
    toast.success('Email client opened');
  };

  const submitLabel = () => {
    if (loading) return <span className="spinner" />;
    const labels = {
      pp_direct:  <><FiCreditCard style={{ marginRight: 6 }} />PhonePe Pay Now</>,
      pp_link:    <><FiLink style={{ marginRight: 6 }} />Generate PhonePe Link</>,
      pp_qr:      <><FiCamera style={{ marginRight: 6 }} />Generate QR</>,
      rzp_direct: <><FiZap style={{ marginRight: 6 }} />Open Razorpay Checkout</>,
      rzp_link:   <><FiLink style={{ marginRight: 6 }} />Generate Razorpay Link</>,
    };
    return labels[tab] || 'Submit';
  };

  const isRzp = tab.startsWith('rzp');

  return (
    <Layout>
      <div className="page-header">
        <h1 className="page-title">Create Payment</h1>
        <p className="page-subtitle">Choose a gateway and method below</p>
      </div>

      <div className="page-body fade-in">
        <div style={{ maxWidth: 720 }}>

          {/* ── Gateway Tab Selector ── */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 0, flexWrap: 'wrap' }}>
            {/* PhonePe column */}
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>
                PhonePe — UPI only
              </div>
              <div className="tab-group">
                {GATEWAY_TABS.filter(t => t.gateway === 'phonepe').map(t => (
                  <button key={t.id}
                    className={`tab-btn ${tab === t.id ? 'active' : ''}`}
                    onClick={() => { setTab(t.id); setResult(null); }}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div style={{ width: 1, background: 'var(--border)', alignSelf: 'stretch' }} />

            {/* Razorpay column */}
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#2c74f6', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
            
                Razorpay — Card / Netbanking / UPI / Wallets
              </div>
              <div className="tab-group">
                {GATEWAY_TABS.filter(t => t.gateway === 'razorpay').map(t => (
                  <button key={t.id}
                    className={`tab-btn ${tab === t.id ? 'active' : ''}`}
                    style={tab === t.id ? { borderColor: '#2c74f6', color: '#2c74f6' } : {}}
                    onClick={() => { setTab(t.id); setResult(null); }}>
                    {t.label}
                    {t.badge && (
                      <span style={{ marginLeft: 6, background: '#2c74f6', color: '#fff', fontSize: 9, padding: '1px 5px', borderRadius: 4 }}>
                        {t.badge}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="alert alert-info" style={{ marginTop: 12, marginBottom: 20 }}>
            <FiInfo style={{ marginRight: 6 }} />{activeTab?.desc}
          </div>

          {/* ── Form ── */}
          <div className="card">
            <div className="two-col">
              <div className="form-group">
                <label className="form-label">Customer Name *</label>
                <input className="form-input" placeholder="Rahul Sharma"
                  value={form.name} onChange={e => updateForm('name', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Mobile Number *</label>
                <input className="form-input" placeholder="9876543210" maxLength={10}
                  value={form.phone} onChange={e => updateForm('phone', e.target.value.replace(/\D/g, ''))} />
              </div>
            </div>

            <div className="two-col">
              <div className="form-group">
                <label className="form-label">Email (optional)</label>
                <input className="form-input" type="email" placeholder="customer@email.com"
                  value={form.email} onChange={e => updateForm('email', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Amount (₹) *</label>
                <input className="form-input" type="number" placeholder="500" min="1" max="500000"
                  value={form.amount} onChange={e => updateForm('amount', e.target.value)} />
              </div>
            </div>

            <div className="two-col">
              <div className="form-group">
                <label className="form-label">
                  <FiCalendar style={{ marginRight: 5, verticalAlign: 'middle' }} />
                  Event Name (optional)
                </label>
                <input className="form-input" placeholder="e.g. Annual Tech Conference 2025"
                  value={form.eventName} onChange={e => updateForm('eventName', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Description (optional)</label>
                <input className="form-input" placeholder="e.g. Website package..."
                  value={form.description} onChange={e => updateForm('description', e.target.value)} />
              </div>
            </div>

            {form.amount && parseFloat(form.amount) > 0 && (
              <div style={{ background: 'var(--bg3)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13 }}>
                💰 Collecting <strong>₹{parseFloat(form.amount).toLocaleString('en-IN')}</strong> from{' '}
                <strong>{form.name || 'customer'}</strong>
                {form.eventName && <> for <strong style={{ color: 'var(--primary)' }}>🎫 {form.eventName}</strong></>}
                {' via '}
                <strong style={{ color: isRzp ? '#2c74f6' : '#6739b7' }}>
                  {isRzp ? 'Razorpay' : 'PhonePe'}
                </strong>
              </div>
            )}

            <div className="flex gap-8">
              <button
                className="btn btn-primary"
                style={{
                  flex: 1, height: 44,
                  ...(isRzp ? { background: '#2c74f6', borderColor: '#2c74f6' } : {}),
                }}
                onClick={handleSubmit}
                disabled={loading}>
                {submitLabel()}
              </button>
              {result && <button className="btn btn-outline btn-sm" onClick={reset}>Reset</button>}
            </div>
          </div>

          {/* ── Results ── */}
          {result && (
            <div className="card fade-in" style={{ marginTop: 16 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, color: 'var(--success)' }}>
                <FiCheckCircle style={{ marginRight: 6 }} />
                {result.type === 'qr'      ? 'QR Code Ready'
                 : result.type === 'link'  ? 'Payment Link Ready'
                 : result.type === 'success' ? 'Payment Confirmed!'
                 : 'Checkout Opened'}
                {result.gateway === 'razorpay' && (
                  <span style={{ marginLeft: 8, background: '#2c74f6', color: '#fff', fontSize: 10, padding: '2px 6px', borderRadius: 4 }}>
                    Razorpay
                  </span>
                )}
              </h3>

              {/* Txn ID */}
              <div style={{
                background: 'var(--bg3)', borderRadius: 8, padding: '8px 12px',
                marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between'
              }}>
                <span style={{ fontSize: 12, color: 'var(--text3)' }}>
                  Transaction ID: <span className="font-mono">{result.txnId}</span>
                </span>
                <button className="copy-btn" onClick={() => copyToClipboard(result.txnId)}>Copy</button>
              </div>

              {/* Razorpay Payment ID */}
              {result.razorpayPaymentId && (
                <div style={{
                  background: '#eff5ff', borderRadius: 8, padding: '8px 12px',
                  marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  border: '1px solid #c2d9ff'
                }}>
                  <span style={{ fontSize: 12, color: '#2c74f6' }}>
                    Razorpay Payment ID: <span className="font-mono">{result.razorpayPaymentId}</span>
                  </span>
                  <button className="copy-btn" onClick={() => copyToClipboard(result.razorpayPaymentId)}>Copy</button>
                </div>
              )}

              {/* Razorpay checkout success */}
              {result.type === 'success' && (
                <div className="alert alert-success" style={{ marginBottom: 16 }}>
                     Payment verified and confirmed via Razorpay. Check Transactions for full details.
                </div>
              )}

              {/* Payment Link (both gateways) */}
              {result.type === 'link' && (
                <div>
                  <div style={{
                    background: 'var(--bg3)', borderRadius: 8, padding: '12px 14px',
                    marginBottom: 12, wordBreak: 'break-all', fontSize: 13,
                    border: '1px solid var(--border)'
                  }}>
                    <div style={{ fontWeight: 600, marginBottom: 6, color: 'var(--text2)', fontSize: 12 }}>
                      🔗 Payment Link{' '}
                      {result.gateway === 'razorpay'
                        ? '— Card / Netbanking / UPI / Wallets'
                        : '— UPI only'}
                    </div>
                    <a href={result.link} target="_blank" rel="noreferrer"
                      style={{ color: 'var(--primary)', textDecoration: 'underline' }}>
                      {result.link}
                    </a>
                  </div>
                  <div className="flex gap-8" style={{ flexWrap: 'wrap', marginBottom: 16 }}>
                    <button className="btn btn-primary btn-sm" onClick={() => copyToClipboard(result.link)}>
                      {copyDone ? '✅ Copied!' : <><FiCopy style={{ marginRight: 6 }} />Copy Link</>}
                    </button>
                    <button className="btn btn-success btn-sm" onClick={shareOnWhatsApp}>
                      <FiMessageCircle style={{ marginRight: 6 }} />WhatsApp
                    </button>
                    <button className="btn btn-info btn-sm" onClick={shareViaEmail}>
                      <FiMail style={{ marginRight: 6 }} />Email
                    </button>
                    <button className="btn btn-outline btn-sm" onClick={() => window.open(result.link, '_blank')}>
                      <FiExternalLink style={{ marginRight: 6 }} />Open Link
                    </button>
                  </div>
                </div>
              )}

              {/* QR Code (PhonePe only) */}
              {result.type === 'qr' && (
                <div>
                  <div style={{ textAlign: 'center', padding: 20, background: '#f8f9fa', borderRadius: 12, marginBottom: 16 }}>
                    {result.qrImage
                      ? <img src={result.qrImage} alt="UPI QR"
                          style={{ width: 260, height: 260, borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                      : <div style={{ width: 260, height: 260, margin: '0 auto', background: '#fff', borderRadius: 8,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          padding: 12, wordBreak: 'break-all', fontSize: 11, color: '#555' }}>
                          {result.qrData}
                        </div>
                    }
                    <div style={{ marginTop: 12, fontWeight: 700, fontSize: 18 }}>
                      ₹{parseFloat(form.amount).toLocaleString('en-IN')}
                    </div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Scan with any UPI app</div>
                  </div>
                  <div className="flex gap-8" style={{ justifyContent: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
                    <button className="btn btn-primary btn-sm" onClick={() => copyToClipboard(result.qrData)}>
                      <FiCopy style={{ marginRight: 6 }} />Copy UPI String
                    </button>
                    <button className="btn btn-success btn-sm" onClick={shareOnWhatsApp}>
                      <FiMessageCircle style={{ marginRight: 6 }} />WhatsApp
                    </button>
                    <button className="btn btn-info btn-sm" onClick={shareViaEmail}>
                      <FiMail style={{ marginRight: 6 }} />Email
                    </button>
                    {result.qrImage && (
                      <button className="btn btn-outline btn-sm" onClick={downloadQR}>
                        <FiDownload style={{ marginRight: 6 }} />Download QR
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* PhonePe Direct redirect */}
              {result.type === 'redirect' && (
                <div style={{ marginBottom: 16 }}>
                  <div className="alert alert-success" style={{ marginBottom: 12 }}>
                    PhonePe checkout opened in a new tab. Ask the customer to complete the payment.
                  </div>
                  <button className="btn btn-outline btn-sm" onClick={() => window.open(result.url, '_blank')}>
                    <FiRefreshCw style={{ marginRight: 6 }} />Re-open Checkout
                  </button>
                </div>
              )}

              <div className="divider" />
              <CheckStatus txnId={result.txnId} gateway={result.gateway} />
            </div>
          )}

        </div>
      </div>
    </Layout>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Inline status poller — works for both PhonePe and Razorpay
// ─────────────────────────────────────────────────────────────────────────────
function CheckStatus({ txnId, gateway }) {
  const [status, setStatus]   = useState(null);
  const [loading, setLoading] = useState(false);

  const check = async () => {
    setLoading(true);
    try {
      const endpoint = gateway === 'razorpay'
        ? `/razorpay/status/${txnId}`
        : `/payment/status/${txnId}`;
      const { data } = await API.get(endpoint);
      setStatus(data.status);
      if      (data.status === 'SUCCESS') toast.success('✅ Payment confirmed!');
      else if (data.status === 'FAILED')  toast.error('❌ Payment failed');
      else                                toast('⏳ Payment still pending...');
    } catch {
      toast.error('Status check failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <button className="btn btn-outline btn-sm" onClick={check} disabled={loading}>
        {loading ? 'Checking...' : <><FiRefreshCw style={{ marginRight: 6 }} />Check Payment Status</>}
      </button>
      {status && (
        <div className={`alert mt-8 ${
          status === 'SUCCESS' ? 'alert-success'
          : status === 'FAILED' ? 'alert-error'
          : 'alert-info'
        }`} style={{ marginTop: 8 }}>
          Payment Status: <strong>{status}</strong>
        </div>
      )}
    </div>
  );
}